import { calcDistance, formatDistance } from "@/utils/distance";
import type { UserRoute } from "@/hooks/useMyRoutes";

interface Props {
  route: UserRoute;
  onClose: () => void;
}

function calcRouteTotalDistance(route: UserRoute): number {
  const completedStops = [...route.stops]
    .filter((s) => s.completed)
    .sort((a, b) => a.orderIndex - b.orderIndex);

  if (completedStops.length < 2) return 0;
  let total = 0;
  for (let i = 0; i < completedStops.length - 1; i++) {
    total += calcDistance(
      completedStops[i].lat,
      completedStops[i].lng,
      completedStops[i + 1].lat,
      completedStops[i + 1].lng,
    );
  }
  return total;
}

function calcCompletionTime(route: UserRoute): string {
  const startedAt = route.startedAt ? new Date(route.startedAt) : null;
  const completedTimes = route.stops
    .filter((s) => s.completed && s.completedAt)
    .map((s) => new Date(s.completedAt!).getTime());

  if (!startedAt || completedTimes.length === 0) return "—";

  const lastCompleted = Math.max(...completedTimes);
  const diffMs = lastCompleted - startedAt.getTime();
  if (diffMs <= 0) return "—";

  const totalMins = Math.round(diffMs / 60000);
  if (totalMins < 60) return `${totalMins} dk`;
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return mins > 0 ? `${hrs} sa ${mins} dk` : `${hrs} sa`;
}

export default function RouteStatsModal({ route, onClose }: Props) {
  const completed = route.stops.filter((s) => s.completed).length;
  const total = route.stops.length;
  const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
  const totalDistance = calcRouteTotalDistance(route);
  const completionTime = calcCompletionTime(route);

  const statCards = [
    {
      icon: "ri-checkbox-circle-line",
      label: "Tamamlanan Durak",
      value: `${completed}/${total}`,
      sub: `%${progress} ilerleme`,
      color: "text-emerald-700 bg-emerald-50 border-emerald-200",
    },
    {
      icon: "ri-route-line",
      label: "Kat Edilen Mesafe",
      value: totalDistance > 0 ? formatDistance(totalDistance) : "—",
      sub: totalDistance > 0 ? "Duraklar arası toplam" : "Henüz mesafe yok",
      color: "text-teal-700 bg-teal-50 border-teal-200",
    },
    {
      icon: "ri-time-line",
      label: "Geçen Süre",
      value: completionTime,
      sub: "Başlangıçtan son durağa",
      color: "text-amber-700 bg-amber-50 border-amber-200",
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-stone-100">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-bold text-stone-900 text-base">{route.name}</h2>
              <p className="text-xs text-stone-500 mt-0.5">Rota İstatistikleri</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-xl cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="px-5 py-5 space-y-3">
          {statCards.map((card) => (
            <div
              key={card.label}
              className={`flex items-center gap-4 p-4 rounded-xl border ${card.color}`}
            >
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                <i className={`${card.icon} text-2xl`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium opacity-70">{card.label}</p>
                <p className="font-bold text-xl leading-tight">{card.value}</p>
                <p className="text-xs opacity-60 mt-0.5">{card.sub}</p>
              </div>
            </div>
          ))}

          {/* Progress bar */}
          <div className="pt-2">
            <div className="flex justify-between text-xs text-stone-500 mb-2">
              <span>Genel İlerleme</span>
              <span>%{progress}</span>
            </div>
            <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Stops detail */}
        <div className="px-5 pb-5">
          <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">
            Duraklar
          </p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {route.stops.map((stop, idx) => (
              <div
                key={stop.venueId}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${
                  stop.completed
                    ? "bg-emerald-50 border border-emerald-100"
                    : "bg-stone-50 border border-stone-100"
                }`}
              >
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                    stop.completed
                      ? "bg-emerald-500 text-white"
                      : "bg-stone-200 text-stone-500"
                  }`}
                >
                  {stop.completed ? <i className="ri-check-line text-xs" /> : idx + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-sm font-medium truncate ${
                      stop.completed ? "text-emerald-800" : "text-stone-700"
                    }`}
                  >
                    {stop.venueName}
                  </p>
                  {stop.completed && stop.completedAt && (
                    <p className="text-xs text-emerald-600">
                      {new Date(stop.completedAt).toLocaleTimeString("tr-TR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      tamamlandı
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
